import { useState, useRef, useEffect, useCallback } from 'react'
import { MapContainer, TileLayer, CircleMarker, Polyline } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { Monitor, X, Globe, LayoutGrid } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import { DATA_CENTRES, SUBMARINE_CABLES, OPERATOR_COLORS } from '../lib/geo-data'

// ── Mini Terminal Panel ──────────────────────────────────────────────────────

interface PanelLine { type: 'input' | 'output' | 'error' | 'system'; text: string; url?: string }

function TerminalText({ text, url }: { text: string; url?: string }) {
  if (url) {
    return <a href={url} target="_blank" rel="noopener" className="hover:underline cursor-pointer">{text}</a>
  }

  // Colourize: URLs, numbers, status keywords, company names
  const colourized = text
    .replace(/(https?:\/\/[^\s]+)/g, '<url>$1</url>')
    .replace(/\b(critical|elevated|outage|error|disrupted|down|failed|offline)\b/gi, '<red>$1</red>')
    .replace(/\b(operational|ok|low|connected|active|healthy|up|online|running|succeeded)\b/gi, '<green>$1</green>')
    .replace(/\b(degraded|guarded|stale|warning|maintenance|pending)\b/gi, '<amber>$1</amber>')
    .replace(/\b(CVSS \d+\.?\d*|\d+ mentions|\d+ results|\d+ articles)\b/g, '<num>$1</num>')
    .replace(/\[([^\]]+)\]/g, '<src>[$1]</src>')

  if (!colourized.includes('<')) return <>{text}</>

  // Parse the simple markup
  const parts: JSX.Element[] = []
  let remaining = colourized
  let key = 0
  const tagRegex = /<(url|red|green|amber|num|src)>(.*?)<\/\1>/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = tagRegex.exec(colourized)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{colourized.slice(lastIndex, match.index)}</span>)
    }
    const [, tag, content] = match
    const cls = tag === 'url' ? 'text-blue-light hover:underline cursor-pointer' :
                tag === 'red' ? 'text-rose-400 font-bold' :
                tag === 'green' ? 'text-emerald font-bold' :
                tag === 'amber' ? 'text-amber font-bold' :
                tag === 'num' ? 'text-cyan-400' :
                tag === 'src' ? 'text-purple-400' : ''
    if (tag === 'url') {
      parts.push(<a key={key++} href={content} target="_blank" rel="noopener" className={cls}>{content}</a>)
    } else {
      parts.push(<span key={key++} className={cls}>{tag === 'src' ? content : content}</span>)
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < colourized.length) {
    parts.push(<span key={key++}>{colourized.slice(lastIndex)}</span>)
  }

  return <>{parts}</>
}

function MiniTerminal({ id, onClose, initialCommand, autoRefreshSecs }: { id: number; onClose: () => void; initialCommand?: string; autoRefreshSecs?: number }) {
  const [lines, setLines] = useState<PanelLine[]>([
    { type: 'system', text: `TERM-${id} ready. Type "help" for commands.` },
  ])
  const [input, setInput] = useState('')
  const [processing, setProcessing] = useState(false)
  const [history, setHistory] = useState<string[]>([])
  const [histIdx, setHistIdx] = useState(-1)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [lines])

  // Run initial command on mount
  useEffect(() => {
    if (initialCommand) {
      setTimeout(() => handleCommand(initialCommand), 500)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-refresh — clear screen then re-run
  useEffect(() => {
    if (!autoRefreshSecs || autoRefreshSecs <= 0 || !initialCommand) return
    const interval = setInterval(() => {
      setLines([{ type: 'system', text: `[auto-refresh ${autoRefreshSecs}s]` }])
      setTimeout(() => handleCommand(initialCommand), 100)
    }, autoRefreshSecs * 1000)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshSecs, initialCommand])

  const addOutput = (text: string, type: PanelLine['type'] = 'output') => {
    setLines(prev => [...prev, { type, text }])
  }

  const handleCommand = useCallback(async (cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return
    setHistory(prev => [trimmed, ...prev.slice(0, 30)])
    setHistIdx(-1)
    addOutput(`> ${trimmed}`, 'input')
    setProcessing(true)

    try {
      const parts = trimmed.split(/\s+/)
      const command = parts[0].toLowerCase()
      const args = parts.slice(1).join(' ')

      if (command === 'clear') { setLines([{ type: 'system', text: 'Cleared.' }]); return }
      if (command === 'help') {
        addOutput('Commands: search, entity, trends, cloud, cves, threat, feeds, db, clear', 'system')
        return
      }

      let endpoint = ''
      let params: Record<string, string> = {}

      switch (command) {
        case 'search': endpoint = '/api/search'; params = { q: args, limit: '8' }; break
        case 'entity': endpoint = `/api/trends/entity/${encodeURIComponent(args)}`; break
        case 'trends': endpoint = '/api/trends/live'; break
        case 'cloud': endpoint = '/api/cloud/status'; break
        case 'cves': endpoint = '/api/cyber/cves'; params = { limit: '5', severity: 'critical' }; break
        case 'threat': endpoint = '/api/cyber/threat-level'; break
        case 'feeds': endpoint = '/api/system/feeds'; break
        case 'db': endpoint = '/api/system/db-stats'; break
        default: addOutput(`Unknown: ${command}`, 'error'); return
      }

      const { data } = await api.get(endpoint, { params })

      // Format output based on command
      if (command === 'search') {
        addOutput(`${data.total} results for "${args}"`, 'system')
        for (const r of (data.results || []).slice(0, 6)) {
          setLines(prev => [...prev, { type: 'output' as const, text: `  [${r.source}] ${r.title?.slice(0, 70)}`, url: r.url || '' }])
        }
      } else if (command === 'trends') {
        for (const c of (data.trending_companies || []).slice(0, 6)) addOutput(`  ${c.name.padEnd(20)} ${c.mentions}`)
      } else if (command === 'cloud') {
        for (const p of (data.providers || [])) {
          const icon = p.overall_status === 'operational' ? '●' : '✗'
          addOutput(`  ${icon} ${p.name?.replace('Amazon Web Services','AWS').replace('Google Cloud Platform','GCP').padEnd(18)} ${p.overall_status}`)
        }
      } else if (command === 'cves') {
        for (const c of (data.cves || [])) addOutput(`  ${c.id} CVSS:${c.cvss_score}${c.exploited_in_wild ? ' [WILD]' : ''}`)
      } else if (command === 'threat') {
        addOutput(`  Level: ${data.level?.toUpperCase()} | CVEs: ${data.critical_cves_7d} | Exploited: ${data.kev_additions_7d}`, 'system')
      } else if (command === 'entity') {
        addOutput(`  ${data.entity}: ${data.total_mentions} mentions`, 'system')
        for (const a of (data.articles || []).slice(0, 4)) addOutput(`    ${a.title?.slice(0, 70)}`)
      } else if (command === 'feeds') {
        addOutput(`  ${data.active_sources} active / ${data.total_sources} total`, 'system')
      } else if (command === 'db') {
        for (const [t, c] of Object.entries(data.tables || {})) addOutput(`  ${t}: ${c}`)
      }
    } catch (err: any) {
      addOutput(`Error: ${err.message}`, 'error')
    } finally {
      setProcessing(false)
    }
  }, [])

  return (
    <div className="h-full flex flex-col rounded-lg border border-emerald/20 bg-[#0a0a0a] overflow-hidden crt-panel-glow">
      {/* Title bar */}
      <div className="flex items-center justify-between px-2 py-0.5 border-b border-emerald/10 bg-[#0d0d0d]">
        <span className="text-[8px] text-emerald/60 font-mono">TERM-{id}</span>
        <button type="button" onClick={onClose} className="text-emerald/40 hover:text-rose text-[10px]" title="Close"><X size={9} /></button>
      </div>

      {/* Output — scrolls up, most recent at bottom */}
      <div className="flex-1 overflow-y-auto px-2 py-1 font-mono text-[9px] crt-scrollbar" onClick={() => inputRef.current?.focus()}>
        {lines.map((line, i) => (
          <div key={i} className={clsx(
            line.type === 'input' ? 'text-amber brightness-125' :
            line.type === 'error' ? 'text-rose-400' :
            line.type === 'system' ? 'text-cyan-400' : 'text-emerald/80'
          )} style={{ minHeight: '1em', whiteSpace: 'pre-wrap' }}><TerminalText text={line.text} url={line.url} /></div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Bloomberg-style execute bar at bottom */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-t border-amber/20 bg-[#0d0d0d]">
        <span className="text-amber text-[9px] font-mono font-bold">{id}{'>'}</span>
        <input ref={inputRef} type="text" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !processing) { handleCommand(input); setInput('') }
            else if (e.key === 'ArrowUp') { e.preventDefault(); if (history.length > 0) { const idx = Math.min(histIdx + 1, history.length - 1); setHistIdx(idx); setInput(history[idx]) } }
            else if (e.key === 'ArrowDown') { e.preventDefault(); if (histIdx > 0) { setHistIdx(histIdx - 1); setInput(history[histIdx - 1]) } else { setHistIdx(-1); setInput('') } }
          }}
          disabled={processing}
          className="flex-1 bg-transparent text-amber border-none outline-none font-mono text-[10px] font-bold"
          autoComplete="off" spellCheck={false} placeholder="Enter command..." title="Execute" />
        {processing && <span className="text-emerald/60 text-[8px] animate-pulse">...</span>}
      </div>
    </div>
  )
}

// ── Map Embed Panel ──────────────────────────────────────────────────────────

function MapPanel({ onClose }: { onClose: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const globeRef = useRef<any>(null)

  useEffect(() => {
    if (!containerRef.current) return

    import('globe.gl').then((mod) => {
      if (!containerRef.current) return
      const Globe = mod.default as any
      const globe = Globe()(containerRef.current)
      const w = containerRef.current.clientWidth
      const h = containerRef.current.clientHeight || 300

      globe
        .globeImageUrl('/globe/earth-blue-marble.jpg')
        .bumpImageUrl('/globe/earth-topology.png')
        .backgroundImageUrl('/globe/night-sky.png')
        .width(w).height(h)
        .pointOfView({ lat: 30, lng: 10, altitude: 2.2 })

      // Data centres
      globe
        .pointsData(DATA_CENTRES)
        .pointLat((d: any) => d.lat).pointLng((d: any) => d.lng)
        .pointAltitude(() => 0.01)
        .pointRadius((d: any) => d.tier === 'hyperscale' ? 0.7 : 0.4)
        .pointColor((d: any) => OPERATOR_COLORS[d.operator] || '#64748b')

      // Cables as arcs
      const arcs = SUBMARINE_CABLES.filter(c => c.points.length >= 2).flatMap(cable =>
        cable.points.slice(0, -1).map((pt, i) => ({
          startLat: pt[0], startLng: pt[1],
          endLat: cable.points[i + 1][0], endLng: cable.points[i + 1][1],
          color: cable.color,
        }))
      )
      globe
        .arcsData(arcs)
        .arcStartLat((d: any) => d.startLat).arcStartLng((d: any) => d.startLng)
        .arcEndLat((d: any) => d.endLat).arcEndLng((d: any) => d.endLng)
        .arcColor((d: any) => d.color)
        .arcStroke(0.3).arcDashLength(0.4).arcDashGap(0.2).arcDashAnimateTime(3000)

      globe.controls().autoRotate = true
      globe.controls().autoRotateSpeed = 0.4
      globeRef.current = globe
    }).catch(err => console.error('Globe panel failed:', err))

    // ResizeObserver — tracks panel size changes (column layout, window resize, panel add/remove)
    const observer = new ResizeObserver(() => {
      if (globeRef.current && containerRef.current) {
        const w = containerRef.current.clientWidth
        const h = containerRef.current.clientHeight
        if (w > 0 && h > 0) {
          globeRef.current.width(w).height(h)
        }
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
      if (globeRef.current) { try { globeRef.current._destructor?.() } catch {} globeRef.current = null }
    }
  }, [])

  return (
    <div className="h-full flex flex-col rounded-lg border border-blue/20 bg-[#0a0a0a] overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 border-b border-blue/10 bg-[#0d0d0d]">
        <span className="text-[9px] text-blue/60 font-mono">3D GLOBE</span>
        <button type="button" onClick={onClose} className="text-blue/40 hover:text-rose text-[10px]" title="Close"><X size={10} /></button>
      </div>
      <div className="flex-1" ref={containerRef} />
    </div>
  )
}

// ── Panel Types ──────────────────────────────────────────────────────────────

type PanelType = { kind: 'terminal'; id: number; initialCommand?: string; autoRefreshSecs?: number } | { kind: 'map'; id: number }
type PresetPanel =
  | { kind: 'terminal'; initialCommand?: string; autoRefreshSecs?: number }
  | { kind: 'map' }

const PRESETS: { name: string; description: string; panels: PresetPanel[]; cols: 2 | 3 | 4 }[] = [
  {
    name: 'CTO Overview',
    description: 'Threat + Cloud + Trends + Globe',
    cols: 2,
    panels: [
      { kind: 'terminal', initialCommand: 'threat', autoRefreshSecs: 120 },
      { kind: 'terminal', initialCommand: 'cloud', autoRefreshSecs: 60 },
      { kind: 'terminal', initialCommand: 'trends', autoRefreshSecs: 300 },
      { kind: 'map' },
    ],
  },
  {
    name: 'Cyber War Room',
    description: 'CVEs + Threat + Cloud + Search',
    cols: 2,
    panels: [
      { kind: 'terminal', initialCommand: 'cves', autoRefreshSecs: 120 },
      { kind: 'terminal', initialCommand: 'threat', autoRefreshSecs: 120 },
      { kind: 'terminal', initialCommand: 'cloud', autoRefreshSecs: 60 },
      { kind: 'terminal', initialCommand: 'feeds', autoRefreshSecs: 300 },
    ],
  },
  {
    name: 'Market Intel',
    description: 'Trends + NVIDIA + Apple + Globe',
    cols: 2,
    panels: [
      { kind: 'terminal', initialCommand: 'trends', autoRefreshSecs: 300 },
      { kind: 'terminal', initialCommand: 'entity NVIDIA', autoRefreshSecs: 300 },
      { kind: 'terminal', initialCommand: 'entity Apple', autoRefreshSecs: 300 },
      { kind: 'map' },
    ],
  },
  {
    name: 'Full Monitor',
    description: '6 panels: threat, cloud, CVEs, trends, feeds, globe',
    cols: 3,
    panels: [
      { kind: 'terminal', initialCommand: 'threat', autoRefreshSecs: 120 },
      { kind: 'terminal', initialCommand: 'cloud', autoRefreshSecs: 60 },
      { kind: 'terminal', initialCommand: 'cves', autoRefreshSecs: 120 },
      { kind: 'terminal', initialCommand: 'trends', autoRefreshSecs: 300 },
      { kind: 'terminal', initialCommand: 'feeds', autoRefreshSecs: 600 },
      { kind: 'map' },
    ],
  },
]

// ── Workspace Component ──────────────────────────────────────────────────────

export default function WorkspacePage() {
  const defaultPreset = PRESETS[0]
  const [panels, setPanels] = useState<PanelType[]>(
    defaultPreset.panels.map((p, i) => ({ ...p, id: i + 1 }))
  )
  const [nextId, setNextId] = useState(defaultPreset.panels.length + 1)
  const [layout, setLayout] = useState<2 | 3 | 4>(defaultPreset.cols)

  const loadPreset = (preset: typeof PRESETS[0]) => {
    setPanels(preset.panels.map((p, i) => ({ ...p, id: nextId + i })))
    setNextId(prev => prev + preset.panels.length)
    setLayout(preset.cols)
  }

  const addPanel = (kind: 'terminal' | 'map') => {
    if (panels.length >= 9) return
    setPanels(prev => [...prev, { kind, id: nextId }])
    setNextId(n => n + 1)
  }

  const removePanel = (id: number) => {
    setPanels(prev => prev.filter(p => p.id !== id))
  }

  const gridClass = layout === 2 ? 'grid-cols-2' : layout === 3 ? 'grid-cols-3' : 'grid-cols-4'

  return (
    <div className="animate-fade-in flex flex-col" style={{ height: 'calc(100vh - 7rem)' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className="flex items-center gap-2">
          <LayoutGrid size={18} className="text-emerald" />
          <h1 className="text-sm font-bold text-text-primary">Workspace</h1>
          <span className="text-[10px] text-text-muted">{panels.length}/9 panels</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Presets */}
          {PRESETS.map(preset => (
            <button key={preset.name} type="button" onClick={() => loadPreset(preset)}
              className="btn-secondary text-[9px] py-0.5 px-2" title={preset.description}>
              {preset.name}
            </button>
          ))}
          <span className="text-text-muted text-[9px] mx-1">|</span>
          <button type="button" onClick={() => addPanel('terminal')} disabled={panels.length >= 9}
            className="btn-secondary text-[9px] py-0.5 px-2 disabled:opacity-30">
            <Monitor size={9} /> +Term
          </button>
          <button type="button" onClick={() => addPanel('map')} disabled={panels.length >= 9}
            className="btn-secondary text-[9px] py-0.5 px-2 disabled:opacity-30">
            <Globe size={9} /> +Globe
          </button>
          <div className="flex bg-surface-card border border-border rounded overflow-hidden">
            {([2, 3, 4] as const).map(n => (
              <button key={n} type="button" onClick={() => setLayout(n)}
                className={clsx('px-1.5 py-0.5 text-[9px]', layout === n ? 'bg-blue text-white' : 'text-text-muted')}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Panel grid — fills remaining height */}
      <div className={clsx('grid gap-2 flex-1 min-h-0', gridClass)}>
        {panels.map(panel => (
          <div key={panel.id} className="min-h-0">
            {panel.kind === 'terminal' ? (
              <MiniTerminal id={panel.id} onClose={() => removePanel(panel.id)}
                initialCommand={panel.initialCommand} autoRefreshSecs={panel.autoRefreshSecs} />
            ) : (
              <MapPanel onClose={() => removePanel(panel.id)} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
